/**
 *  @swagger
 *  definitions:
 *    RegattaNotificationParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated RegattaNotification
 *          type: string
 *        Type:
 *          description: Type of the RegattaNotification
 *          $ref: '#/components/enums/RegattaNotificationType'
 *        Data:
 *          description: Data of the RegattaNotification
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaNotificationCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaNotificationParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Type
 *            - Data
 *
 *  @swagger
 *  definitions:
 *    RegattaNotificationQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaNotification
 *              type: string
 *        - $ref: '#/definitions/RegattaNotificationParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaNotificationUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaNotificationQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaNotification:
 *      allOf:
 *        - $ref: '#/definitions/RegattaNotificationCreateParams'
 *        - $ref: '#/definitions/RegattaNotificationUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaNotification:
 *        allOf:
 *          - $ref: '#/definitions/RegattaNotification'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaNotification
 *                $ref: '#/components/schemas/Regatta'
 *              Crewmembers:
 *                description: Crewmembers of the RegattaNotification
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Crewmember'
 */

// Enums
import {
  RegattaNotificationType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
  $RegattaNotificationData,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Data: $RegattaNotificationData;
  Type: RegattaNotificationType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Crewmembers?: Array<$Model<'Crewmember'>>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Data: {
    allowNull: false,
    type: Sequelize.JSON,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaNotificationType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaNotification'> => {
  const RegattaNotification = stack.define(
    'RegattaNotification',
    fields(Sequelize),
    {
      tableName: 'RegattaNotifications',
    },
  );

  RegattaNotification.associate = (models: $ModelClasses) => {
    models.RegattaNotification.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaNotification.belongsToMany(
      models.Crewmember,
      {
        as: 'Crewmembers',
        foreignKey: 'RegattaNotificationId',
        otherKey: 'CrewmemberId',
        through: models.CrewmemberRegattaNotification,
      },
    );
  };

  RegattaNotification.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'DESC']],
    },
  );

  return RegattaNotification;
});
